// MaturaDifferenceSquaresClosedTask.js
// Zadanie maturalne zamknięte: (ax ± cy)^2 - (ax ∓ cy)^2
// Dynamiczny generator z losowymi współczynnikami i typowymi błędami.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { AlternativeExplanation } from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const LETTERS = ["A", "B", "C", "D"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function coeffToXYLatex(k) {
  if (k === 1) return "xy";
  if (k === -1) return "-xy";
  return `${k}xy`;
}

function coeffToY2Latex(k) {
  if (k === 1) return "y^2";
  if (k === -1) return "-y^2";
  return `${k}y^2`;
}

// ✅ NOWE: współczynnik przy y (bez kwadratu)
function coeffToYLatex(k) {
  if (k === 0) return "0";
  if (k === 1) return "y";
  if (k === -1) return "-y";
  return `${k}y`;
}

// ---------------------------------------------------------------------------
// GENERATOR
// ---------------------------------------------------------------------------

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 6:
 *   (3x + y)^2 - (3x - y)^2  →  A. 12xy
 */
function createOriginalTask() {
  const aCoeff = 3;
  const yCoeff = 1;
  const plusFirst = true;
  const exprLatex = "(3x + y)^{2} - (3x - y)^{2}";
  const signedCoeff = 12;

  const options = [
    { label: "A", latex: "12xy", isCorrect: true },
    { label: "B", latex: "-12xy", isCorrect: false },
    { label: "C", latex: "2y^2", isCorrect: false },
    { label: "D", latex: "4y^2", isCorrect: false },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaDifferenceSquaresClosedTask",
    data: {
      aCoeff,
      yCoeff,
      plusFirst,
      exprLatex,
      signedCoeff,
      resultLatex: "12xy",
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 6,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  // współczynnik przy x: 2,3,4,5
  const aCoeff = pick([2, 3, 4, 5]);
  // współczynnik przy y: 1..5 (nie zawsze 1)
  const yCoeff = pick([1, 2, 3, 4, 5]);

  // wariant kolejności:
  // plusFirst = true  → (ax + cy)^2 - (ax - cy)^2  → dodatni wynik
  // plusFirst = false → (ax - cy)^2 - (ax + cy)^2  → ujemny wynik
  const plusFirst = Math.random() < 0.5;

  const ax = `${aCoeff}x`;
  const cy = yCoeff === 1 ? "y" : `${yCoeff}y`;

  const plusBracket = `(${ax} + ${cy})`;
  const minusBracket = `(${ax} - ${cy})`;

  const firstBracket = plusFirst ? plusBracket : minusBracket;
  const secondBracket = plusFirst ? minusBracket : plusBracket;

  const exprLatex = `${firstBracket}^{2} - ${secondBracket}^{2}`;

  const baseCoeff = 4 * aCoeff * yCoeff;
  const signedCoeff = (plusFirst ? 1 : -1) * baseCoeff;

  const correctLatex = coeffToXYLatex(signedCoeff);

  // ── distractor 1: poprawny wzór, ale z przeciwnym znakiem ──
  const oppositeLatex = coeffToXYLatex(-signedCoeff);

  // ── distractory kwadratowe: błąd na współczynnikach przy y^2 ──
  const signY2 = plusFirst ? 1 : -1;
  const twoC2Coeff = signY2 * 2 * yCoeff * yCoeff;
  const fourC2Coeff = signY2 * 4 * yCoeff * yCoeff;

  const twoY2Latex = coeffToY2Latex(twoC2Coeff);
  const fourY2Latex = coeffToY2Latex(fourC2Coeff);

  const optionsRaw = [
    { latex: correctLatex, isCorrect: true },
    { latex: oppositeLatex, isCorrect: false },
    { latex: twoY2Latex, isCorrect: false },
    { latex: fourY2Latex, isCorrect: false },
  ];

  const shuffled = shuffle(optionsRaw).map((o, idx) => ({
    label: LETTERS[idx],
    latex: o.latex,
    isCorrect: o.isCorrect,
  }));

  const correctIndex = shuffled.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaDifferenceSquaresClosedTask",
    data: {
      aCoeff,
      yCoeff,
      plusFirst,
      exprLatex,
      signedCoeff,
      resultLatex: correctLatex,
      options: shuffled,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_6" },
  };
}

// ---------------------------------------------------------------------------
// RENDER TREŚCI
// ---------------------------------------------------------------------------

export function renderTask({ data }) {
  const { exprLatex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Dla każdej liczby rzeczywistej <InlineMath math={'x'} /> i dla każdej
        liczby rzeczywistej <InlineMath math={'y'} /> wartość wyrażenia{" "}
        <InlineMath math={exprLatex} /> jest równa wartości wyrażenia:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// INPUT (ABCD)
// ---------------------------------------------------------------------------

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ---------------------------------------------------------------------------
// SPRAWDZANIE / ODPOWIEDZI
// ---------------------------------------------------------------------------

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// WYJAŚNIENIE
// ---------------------------------------------------------------------------

export function renderExplanation({ data }) {
  const { aCoeff, yCoeff, plusFirst, exprLatex, signedCoeff } = data;

  const ax = `${aCoeff}x`;
  const cy = yCoeff === 1 ? "y" : `${yCoeff}y`;

  const plusBracket = `(${ax} + ${cy})`;
  const minusBracket = `(${ax} - ${cy})`;

  const aBracket = plusFirst ? plusBracket : minusBracket;
  const bBracket = plusFirst ? minusBracket : plusBracket;

  // a-b = ±2cy, a+b = 2ax
  const diffCoeff = (plusFirst ? 1 : -1) * 2 * yCoeff; // ✅ tu jest współczynnik przy y
  const sumCoeff = 2 * aCoeff;

  // ✅ poprawny zapis: (±2c)y, nie "±2y"
  const diffLatex = coeffToYLatex(diffCoeff);
  const sumLatex = `${sumCoeff}x`;

  const productLatex = `${diffLatex}\\cdot ${sumLatex}`;
  const resultLatex = coeffToXYLatex(signedCoeff);
  const xSquaredTerm = `${aCoeff * aCoeff}x^2`;
  const ySquaredTerm = `${yCoeff * yCoeff}y^2`;
  const crossTerm = `${2 * aCoeff * yCoeff}xy`;
  const firstCrossSign = plusFirst ? "+" : "-";
  const secondCrossSign = plusFirst ? "-" : "+";
  const firstExpanded = `${xSquaredTerm}${firstCrossSign}${crossTerm}+${ySquaredTerm}`;
  const secondExpanded = `${xSquaredTerm}${secondCrossSign}${crossTerm}+${ySquaredTerm}`;
  const afterRemovingParentheses = `${firstExpanded}-${xSquaredTerm}${firstCrossSign}${crossTerm}-${ySquaredTerm}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Korzystamy ze wzoru na różnicę kwadratów:
      </div>
      <BlockMath math={'a^2 - b^2 = (a - b)(a + b)'} />

      <div>
        <strong>Krok 2.</strong> Podstawiamy{" "}
        <InlineMath math={`a = ${aBracket}`} /> oraz{" "}
        <InlineMath math={`b = ${bBracket}`} /> do wyrażenia{" "}
        <InlineMath math={exprLatex} />:
      </div>
      <BlockMath math={`${exprLatex} = a^2 - b^2`} />

      <div>
        <strong>Krok 3.</strong> Obliczamy{" "}
        <InlineMath math={'a - b'} /> oraz{" "}
        <InlineMath math={'a + b'} />:
      </div>
      <BlockMath math={`${aBracket} - ${bBracket} = ${diffLatex}`} />
      <BlockMath math={`${aBracket} + ${bBracket} = ${sumLatex}`} />

      <div>
        <strong>Krok 4.</strong> Zapisujemy iloczyn{" "}
        <InlineMath math={'(a - b)(a + b)'} /> i upraszczamy:
      </div>
      <BlockMath math={`${productLatex} = ${resultLatex}`} />

      <div>
        <strong>Wniosek.</strong>{" "}
        <InlineMath math={`${exprLatex} = ${resultLatex}`} />, więc poprawną
        odpowiedzią jest <InlineMath math={resultLatex} />.
      </div>

      <AlternativeExplanation
        solution={{
          title: "Inny sposób: rozwijamy oba kwadraty",
          intro: "Różnica kwadratów jest tutaj najszybsza, ale możemy też osobno zastosować wzory na kwadrat sumy i kwadrat różnicy.",
          steps: [
            {
              title: "Rozwijamy oba kwadraty.",
              latex: `\\begin{aligned}${aBracket}^2&=${firstExpanded}\\\\[0.55em]${bBracket}^2&=${secondExpanded}\\end{aligned}`,
            },
            {
              title: "Odejmujemy drugie wyrażenie, opuszczamy nawias i redukujemy wyrazy podobne.",
              latex: `\\begin{aligned}&${exprLatex}=(${firstExpanded})-(${secondExpanded})=\\\\[0.65em]&=${afterRemovingParentheses}=${resultLatex}\\end{aligned}`,
            },
          ],
          note: "Otrzymujemy ten sam wynik, ale metoda z różnicą kwadratów wymaga mniej rachunków.",
        }}
      />
    </div>
  );
}
